import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { ANALYTICS_PARAMS } from "@/features/analytics/models";
import {
  DEFAULT_PRICE_VARIANT,
  IS_CALL_CENTER_PARAM_KEY,
} from "@/features/onboarding/models";
import {
  COUPON_CODE_PARAM,
  useOnboardingStore,
} from "@/features/onboarding/stores";
import { CATEGORY_SLUG } from "@/shared/domains";
import type { AttributionData } from "@/shared/lib/attribution-cookie";
import {
  clearPriceVariantCookie,
  getAttributionCookie,
  getPriceVariantCookie,
  setAttributionCookie,
  setPriceVariantCookie,
} from "@/shared/lib/attribution-cookie";

const PRICE_VARIANT_PARAM = "price_variant";

const EVERFLOW_KEYS = [
  ANALYTICS_PARAMS.EVERFLOW_ORDER_ID,
  ANALYTICS_PARAMS.EVERFLOW_AFFILIATE_ID,
  ANALYTICS_PARAMS.EF_TRANSACTION_ID,
  ANALYTICS_PARAMS.EVERFLOW_SOURCE_ID,
  ANALYTICS_PARAMS.EVERFLOW_UID,
  ANALYTICS_PARAMS.EVERFLOW_SUB1,
  ANALYTICS_PARAMS.EVERFLOW_SUB2,
  ANALYTICS_PARAMS.EVERFLOW_SUB3,
  ANALYTICS_PARAMS.EVERFLOW_SUB4,
  ANALYTICS_PARAMS.EVERFLOW_SUB5,
] as const;

const UTM_KEYS = [
  ANALYTICS_PARAMS.UTM_CAMPAIGN,
  ANALYTICS_PARAMS.UTM_SOURCE,
  ANALYTICS_PARAMS.UTM_MEDIUM,
  ANALYTICS_PARAMS.UTM_CONTENT,
] as const;

const ALL_SOURCE_KEYS = [...EVERFLOW_KEYS, ...UTM_KEYS] as const;

export const useStoreUrlParams = () => {
  const [searchParams] = useSearchParams();

  const setUrlParams = useOnboardingStore((state) => state.setUrlParams);
  const setPriceVariant = useOnboardingStore((state) => state.setPriceVariant);
  const setCouponCode = useOnboardingStore((state) => state.setCouponCode);
  const setCheckoutDiscount = useOnboardingStore(
    (state) => state.setCheckoutDiscount,
  );
  const resetSelectedProducts = useOnboardingStore(
    (state) => state.resetSelectedProducts,
  );
  const setUrlParamsResolved = useOnboardingStore(
    (state) => state.setUrlParamsResolved,
  );
  const setTargetCategory = useOnboardingStore(
    (state) => state.setTargetCategory,
  );
  const setTargetProduct = useOnboardingStore(
    (state) => state.setTargetProduct,
  );
  const categoryParam = searchParams.get("category");
  const productParam = searchParams.get("product");

  useEffect(() => {
    const previousPriceVariant =
      useOnboardingStore.getState().urlParams.price_variant;

    // A call center agent reuses one browser across leads, so a call center
    // link carries the lead's full pricing context: no price_variant in it
    // means default prices, never the previous lead's variant from the cookie.
    const isCallCenterLink =
      searchParams.get(IS_CALL_CENTER_PARAM_KEY) === "true";
    const existingCookie = getAttributionCookie();
    const hasEverflowParams = EVERFLOW_KEYS.some((key) =>
      searchParams.has(key),
    );
    const hasUtmParams = UTM_KEYS.some((key) => searchParams.has(key));
    const cioId = searchParams.get(ANALYTICS_PARAMS.CUSTOMER_IO_ID);

    if (hasEverflowParams) {
      const newCookieData = Object.fromEntries(
        ALL_SOURCE_KEYS.flatMap((key) => {
          const value = searchParams.get(key);
          return value !== null ? [[key, value]] : [];
        }),
      ) as AttributionData;

      const existingTid = existingCookie?.[ANALYTICS_PARAMS.EF_TRANSACTION_ID];
      if (
        existingTid &&
        !isCallCenterLink &&
        !newCookieData[ANALYTICS_PARAMS.EF_TRANSACTION_ID]
      ) {
        newCookieData[ANALYTICS_PARAMS.EF_TRANSACTION_ID] = existingTid;
      }

      const effectiveCioId = cioId ?? existingCookie?.cio_id;
      if (effectiveCioId) {
        newCookieData[ANALYTICS_PARAMS.CUSTOMER_IO_ID] = effectiveCioId;
      }

      setAttributionCookie(newCookieData);

      const merged = new URLSearchParams(searchParams);
      const transactionId = newCookieData[ANALYTICS_PARAMS.EF_TRANSACTION_ID];
      if (transactionId) {
        merged.set(ANALYTICS_PARAMS.EF_TRANSACTION_ID, transactionId);
      }
      if (effectiveCioId) {
        merged.set(ANALYTICS_PARAMS.CUSTOMER_IO_ID, effectiveCioId);
      }
      setUrlParams(merged);
    } else if (hasUtmParams) {
      const updatedCookie: AttributionData = {
        ...(existingCookie ?? {}),
        ...Object.fromEntries(
          UTM_KEYS.flatMap((key) => {
            const value = searchParams.get(key);
            return value !== null ? [[key, value]] : [];
          }),
        ),
      };

      if (cioId) {
        updatedCookie[ANALYTICS_PARAMS.CUSTOMER_IO_ID] = cioId;
      }

      setAttributionCookie(updatedCookie);

      const merged = new URLSearchParams(searchParams);
      Object.entries(updatedCookie).forEach(([key, value]) => {
        if (value !== undefined) {
          merged.set(key, String(value));
        }
      });
      setUrlParams(merged);
    } else if (cioId) {
      const updatedCookie = {
        ...(existingCookie ?? {}),
        [ANALYTICS_PARAMS.CUSTOMER_IO_ID]: cioId,
      };
      setAttributionCookie(updatedCookie);

      const merged = new URLSearchParams(searchParams);
      Object.entries(updatedCookie).forEach(([key, value]) => {
        if (value !== undefined) {
          merged.set(key, String(value));
        }
      });
      setUrlParams(merged);
    } else {
      if (existingCookie) {
        const merged = new URLSearchParams(searchParams);
        Object.entries(existingCookie).forEach(([key, value]) => {
          if (value !== undefined) {
            merged.set(key, String(value));
          }
        });
        setUrlParams(merged);
      } else {
        setUrlParams(searchParams);
      }
    }

    const targetCategory = Object.values(CATEGORY_SLUG).find(
      (slug) => slug === categoryParam,
    );

    if (targetCategory) {
      setTargetCategory(targetCategory);
    }

    if (productParam) {
      setTargetProduct(productParam);
    }

    // Independent of Everflow/UTM attribution: which funnel a visitor is on
    // is carried purely as a URL param from the landing page's CTA, and
    // persisted in its own cookie so it survives reloads later in onboarding.
    const priceVariantParam = searchParams.get(PRICE_VARIANT_PARAM);
    const forcesDefaultPrices = priceVariantParam === DEFAULT_PRICE_VARIANT;
    if (priceVariantParam && !forcesDefaultPrices) {
      setPriceVariantCookie(priceVariantParam);
    } else if (isCallCenterLink || forcesDefaultPrices) {
      clearPriceVariantCookie();
    }
    const effectivePriceVariant = forcesDefaultPrices
      ? null
      : (priceVariantParam ??
        (isCallCenterLink ? null : getPriceVariantCookie()));
    if ((previousPriceVariant ?? null) !== (effectivePriceVariant ?? null)) {
      resetSelectedProducts();
    }
    if (effectivePriceVariant) {
      setPriceVariant(effectivePriceVariant);
    } else {
      setPriceVariant(undefined);
    }

    // The persisted selection carries each price's discount as fetched, so a
    // new coupon must drop it for the pricing screens to reselect from the
    // coupon-adjusted prices.
    const couponCodeParam = searchParams.get(COUPON_CODE_PARAM);
    if (
      couponCodeParam &&
      couponCodeParam !== useOnboardingStore.getState().couponCode
    ) {
      resetSelectedProducts();
      setCheckoutDiscount(undefined);
      setCouponCode(couponCodeParam);
    }

    setUrlParamsResolved();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
};
